// The confirmation link from the trial sign-up email (TGCLOUD-1844). Choosing
// a password here is what creates the workspace.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import AuthLayout from 'src/components/UI/auth/AuthLayout';
import PasswordInput, {
  PasswordStrengthAlert
} from 'src/components/UI/input/PasswordInput';
import { validatePasswordStrength } from 'src/util/util';

const MIN = 10;

export default function TrialVerify() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [pending, setPending] = React.useState(null); // null loading, false invalid
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    axios
      .post(APICONSTANTS.trialVerify, { token })
      .then((res) => setPending(res.data))
      .catch(() => setPending(false));
  }, [token]);

  const ok =
    password.length >= MIN &&
    password === confirm &&
    validatePasswordStrength(password);

  const submit = (e) => {
    e.preventDefault();
    if (!ok || busy) return;
    setBusy(true);
    setError(null);
    axios
      .post(APICONSTANTS.trialComplete, { token, password })
      .then(() => {
        setDone(true);
        setTimeout(() => navigate(CONSTANTS.signinPath), 4000);
      })
      .catch((err) =>
        setError(
          err?.response?.data?.message ||
            'Your workspace could not be created. Please try again.'
        )
      )
      .finally(() => setBusy(false));
  };

  return (
    <AuthLayout variant="narrow">
      <Box sx={{ width: '100%' }}>
        {pending === null && <CircularProgress size={24} />}
        {pending === false && (
          <Alert severity="error">
            This link has expired or has already been used. Sign up again, or if
            you have already created your workspace, sign in.
          </Alert>
        )}
        {done && (
          <Alert severity="success">
            Your workspace for <b>{pending?.organisation}</b> is ready. Taking
            you to sign in…
          </Alert>
        )}
        {pending && !done && (
          <form onSubmit={submit}>
            <Stack spacing={2}>
              <Typography variant="h6">Welcome, {pending.firstName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a password for {pending.email} to create the{' '}
                <b>{pending.organisation}</b> workspace.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <PasswordInput
                name="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrengthAlert password={password} />
              <PasswordInput
                name="confirm"
                label="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                error={!!confirm && confirm !== password}
                helperText={
                  confirm && confirm !== password
                    ? 'The passwords do not match'
                    : `At least ${MIN} characters`
                }
              />
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!ok || busy}
              >
                {busy ? <CircularProgress size={20} /> : 'Create my workspace'}
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </AuthLayout>
  );
}
